import type { PromotionClient } from '@core/types'
import type { CombinedGame, Promotion } from '@promotion/types'

type ActiveOfferData = PromotionClient.components['schemas']['ActiveOfferData']
type ActivatableOfferData = PromotionClient.components['schemas']['ActivatableOfferData']
type RequirementType = 'Payment' | 'Spend'

export interface PlayerOfferRewardContext {
    requirementMessage: (type: RequirementType, amount: string) => string
}

const FOREIGN_MODEL: Record<PromotionClient.BonusType, string> = {
    CASH: 'Bonus',
    SPINS: 'FreeSpinsConfiguration',
    BINGO_TICKETS: 'BingoFreeTicketConfiguration',
    SCRATCHCARDS: 'ScratchCardConfiguration',
    WHEEL_OF_FORTUNE: 'WheelOfFortuneConfiguration',
    PHYSICAL: 'PhysicalPrizeConfiguration',
    DROP_THE_BALL: 'DropTheBallConfiguration',
}

export function bonusTypeOf(reward: Promotion): PromotionClient.BonusType | undefined {
    return (Object.keys(FOREIGN_MODEL) as PromotionClient.BonusType[]).find(type => FOREIGN_MODEL[type] === reward.foreign_model)
}

const REQUIREMENT_TYPE: Partial<Record<PromotionClient.PromotionRequirementType, RequirementType>> = {
    PAYMENT: 'Payment',
    SPEND: 'Spend',
}

const TERMS_REFERENCE_PARAM = {
    promotions: 'promotionReferenceId',
    bonuses: 'bonusReferenceId',
} as const

// PS links the full-terms API endpoint; the terms dialog is opened by its query params instead.
function toTermsHyperlink(termsUrl: string | null): string | undefined {
    const match = termsUrl?.match(/\/(promotions|bonuses)\/([^/]+)\/full-terms$/)
    if (!match) return

    const param = TERMS_REFERENCE_PARAM[match[1] as keyof typeof TERMS_REFERENCE_PARAM]

    return `?terms=true&${param}=${match[2]}`
}

function toCombinedGames(games: ActiveOfferData['games']): CombinedGame[] | undefined {
    if (!games.length) return

    return games.map(game => ({ slug: game.slug, name: game.name ?? '', amount: '' }))
}

/**
 * Maps the shared PS list fields onto the legacy reward shape, mirroring the monolith's
 * `PromotionClient::generate*RewardData` so the bonus cards keep working unchanged.
 */
function toReward(item: ActiveOfferData | ActivatableOfferData, status: string, context: PlayerOfferRewardContext): Promotion {
    const foreignModel = FOREIGN_MODEL[item.rewardType]
    const requirement = item.paymentRequirement
    const requirementType = requirement ? REQUIREMENT_TYPE[requirement.type] : undefined

    return {
        id: item.id,
        external_id: item.id,
        name: item.name ?? undefined,
        title: item.title ?? undefined,
        type: foreignModel,
        foreign_model: foreignModel,
        foreign_model_id: null,
        max_reclaimable_interval_iterations: null,
        trigger_type: '',
        start_at: item.createdAt,
        claimed_at: item.createdAt,
        expires_at: item.expiresAt,
        reward_image_path: item.imagePath ?? '',
        terms_hyperlink: toTermsHyperlink(item.termsUrl),
        cancel_url: item.cancelUrl,
        status,
        staff_only: false,
        requirement_type: requirementType ?? '',
        requirement: requirement?.amount,
        requirement_message: requirement && requirementType ? context.requirementMessage(requirementType, requirement.amount) : undefined,
    }
}

export function toActiveReward(item: ActiveOfferData, context: PlayerOfferRewardContext): Promotion {
    if (item.displayStatus === 'AWAITING_REQUIREMENT_FULFILLMENT') {
        return toReward(item, 'Pending requirement', context)
    }

    return {
        ...toReward(item, 'Active', context),
        playerBonusId: item.id,
        player_offer_promotion_bonus_id: item.playerOfferPromotionBonusId ?? undefined,
        turnover_multiplier: item.wagering?.multiplier,
        turnover_requirement: item.wagering?.target,
        turnover_progress: item.wagering?.wagered,
        combined_games: toCombinedGames(item.games),
    }
}

export function toActivatableReward(item: ActivatableOfferData, context: PlayerOfferRewardContext): Promotion {
    const status = item.displayStatus === 'REJECTED_REACTIVATABLE' ? 'Undecided' : 'Enqueued'

    return {
        ...toReward(item, status, context),
        activate_url: item.activateUrl,
    }
}
